/**
 * The pure state machine behind the answer wizard (FRONT-3155): one question
 * at a time, per-question Skip, Back / Next, and what goes out when the person
 * sends. Surface-agnostic on purpose — the Builder wires it today and the chat
 * is meant to reuse it; nothing in here knows about either.
 *
 * Why a pure module: the frontend suite runs under plain node with no DOM, so
 * every rule the wizard has (bounds, "skip advances", "an Other with no text
 * is not an answer") is only provable here, never through a click.
 */

/** The sentinel a selection carries for the auto-added free-text row. Same
 *  value the chat card uses, so a surface reusing PendingView agrees with it. */
export const WIZARD_OTHER = '__other__';

export interface WizardQuestion {
  question: string;
  header?: string;
  multiSelect: boolean;
  options: Array<{ label: string; description?: string }>;
}

/** Everything the person has done so far, index-aligned with the questions. */
export interface WizardValue {
  /** The question on screen. Always within [0, count - 1]. */
  step: number;
  selected: string[][];
  /** Free text of the "Other" row, per question (only used when allowOther). */
  others: string[];
  skipped: boolean[];
}

export type EntryState = 'answered' | 'skipped' | 'open';

/** One question's outcome on the wire. `null` = left open. */
export type WizardEntry = { selected: string[]; other?: string } | { skipped: true } | null;

export function emptyWizardValue(count: number): WizardValue {
  return {
    step: 0,
    selected: Array.from({ length: count }, () => []),
    others: Array.from({ length: count }, () => ''),
    skipped: Array.from({ length: count }, () => false),
  };
}

/** Pure — keeps a value usable when the question count changed under it. */
export function fitWizardValue(value: WizardValue | undefined, count: number): WizardValue {
  if (!value || value.selected.length !== count) return emptyWizardValue(count);
  return { ...value, step: clampStep(value.step, count) };
}

export function clampStep(step: number, count: number): number {
  if (count <= 0) return 0;
  return Math.min(Math.max(0, Math.trunc(step) || 0), count - 1);
}

/** "2 of 4" — the progress marker. */
export function progressLabel(step: number, count: number): string {
  if (count <= 0) return '';
  return `${clampStep(step, count) + 1} of ${count}`;
}

/**
 * Back / Next availability, with the reason for every disabled state — one
 * decision, never two expressions (CLAUDE.md, FRONT-1670). `onLast` tells the
 * caller Next means "done" there, not "advance".
 */
export function navState(
  step: number,
  count: number,
): { backDisabled: boolean; backReason?: string; onLast: boolean } {
  const s = clampStep(step, count);
  const backDisabled = s === 0;
  return {
    backDisabled,
    ...(backDisabled ? { backReason: 'This is the first question' } : {}),
    onLast: s >= count - 1,
  };
}

export function goBack(value: WizardValue): WizardValue {
  return { ...value, step: clampStep(value.step - 1, value.selected.length) };
}

/** Jump straight to a question (the progress dots are buttons). */
export function goToStep(value: WizardValue, step: number): WizardValue {
  return { ...value, step: clampStep(step, value.selected.length) };
}

export function goNext(value: WizardValue): WizardValue {
  return { ...value, step: clampStep(value.step + 1, value.selected.length) };
}

/** Pure — has this question a usable answer? An Other with no text is not one. */
export function isAnswered(value: WizardValue, qi: number): boolean {
  if (value.skipped[qi]) return false;
  const sel = value.selected[qi] ?? [];
  const real = sel.filter((s) => s !== WIZARD_OTHER);
  if (sel.includes(WIZARD_OTHER) && (value.others[qi] ?? '').trim().length > 0) return true;
  return real.length > 0;
}

export function entryState(value: WizardValue, qi: number): EntryState {
  if (value.skipped[qi]) return 'skipped';
  return isAnswered(value, qi) ? 'answered' : 'open';
}

/** Picking an option answers the question, so it un-skips it. */
export function toggleOption(
  value: WizardValue,
  qi: number,
  label: string,
  multiSelect: boolean,
): WizardValue {
  const cur = value.selected[qi] ?? [];
  const next = multiSelect
    ? cur.includes(label)
      ? cur.filter((l) => l !== label)
      : [...cur, label]
    : [label];
  return {
    ...value,
    selected: value.selected.map((s, i) => (i === qi ? next : s)),
    skipped: value.skipped.map((s, i) => (i === qi ? false : s)),
  };
}

export function setOtherText(value: WizardValue, qi: number, text: string): WizardValue {
  return { ...value, others: value.others.map((o, i) => (i === qi ? text : o)) };
}

/**
 * Skip toggles. Turning it ON records the skip, drops any pick (a question is
 * answered OR skipped, never both) and moves to the next question — "Skip
 * records the skip and moves on". Turning it OFF stays put, so the person can
 * then pick an option.
 */
export function toggleSkip(value: WizardValue, qi: number): WizardValue {
  const turningOn = !value.skipped[qi];
  const base: WizardValue = {
    ...value,
    skipped: value.skipped.map((s, i) => (i === qi ? turningOn : s)),
    selected: turningOn ? value.selected.map((s, i) => (i === qi ? [] : s)) : value.selected,
  };
  return turningOn ? goNext(base) : base;
}

/**
 * What the one forward button does on the current question (eddy, 2026-10-26:
 * "when a question is unanswered, let Next be Skip"). There is no separate
 * Skip button:
 * - an open question, where skipping is allowed: "Skip" — records the skip and moves on
 * - otherwise, with two or more questions: "Next", or "Done" on the last one
 * - otherwise (a single question that is answered, skipped, or cannot be skipped): nothing
 */
export type ForwardAction = 'skip' | 'next' | 'done' | 'none';

export function forwardAction(value: WizardValue, allowSkip: boolean): ForwardAction {
  const count = value.selected.length;
  if (count <= 0) return 'none';
  const step = clampStep(value.step, count);
  if (allowSkip && entryState(value, step) === 'open') return 'skip';
  if (count === 1) return 'none';
  return step >= count - 1 ? 'done' : 'next';
}

export interface WizardCounts {
  total: number;
  answered: number;
  skipped: number;
  open: number;
}

export function wizardCounts(value: WizardValue): WizardCounts {
  const total = value.selected.length;
  let answered = 0;
  let skipped = 0;
  for (let i = 0; i < total; i++) {
    const s = entryState(value, i);
    if (s === 'answered') answered++;
    else if (s === 'skipped') skipped++;
  }
  return { total, answered, skipped, open: total - answered - skipped };
}

/** The entries that go on the wire: answered, skipped, or `null` (still open). */
export function wizardEntries(value: WizardValue): WizardEntry[] {
  return value.selected.map((sel, qi) => {
    const state = entryState(value, qi);
    if (state === 'skipped') return { skipped: true as const };
    if (state === 'open') return null;
    const real = sel.filter((s) => s !== WIZARD_OTHER);
    const other = sel.includes(WIZARD_OTHER) ? (value.others[qi] ?? '').trim() : '';
    return { selected: real, ...(other ? { other } : {}) };
  });
}
